// @flow
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Step,
  StepButton,
  Stepper,
  TextField,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
  Done as DoneIcon,
  DoneAll as DoneAllIcon,
  Edit as EditIcon,
  ExpandMore as ExpandMoreIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  AccountBox as BadgeIcon,
  Home as HomeIcon,
  LocationCity as LocationCityIcon,
  Flag as FlagIcon,
  CalendarToday as CalendarIcon,
  Wc as GenderIcon,
  SkipNext as NextIcon,
} from '@material-ui/icons';
import { format } from 'date-fns';
import DateFnsUtils from '@date-io/date-fns';
import {
  DatePicker,
  MuiPickersUtilsProvider,
} from '@material-ui/pickers';
import { Autocomplete } from '@material-ui/lab';
import { Formik } from 'formik';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { countries, getProvincesOfCountry, getCitiesOfProvince } from '../data/countries';
import Country from '../model/Country';
import SignProcess from '../model/SignProcess';
import { toSentence } from '../util/string';
import { isNotValidEmail, isNotValidName, isNotValidPhone } from '../util/validator';
import DataValidationConfirmDialog from './DataValidationConfirmDialog';

const useStyles = makeStyles((theme) => ({
  draft: {
    color: theme.palette.secondary['900'],
  },
  grey: {
    color: theme.palette.text.secondary,
  },
  error: {
    color: theme.palette.error.main,
  },
  heading: {
    fontSize: theme.typography.pxToRem(15),
    flexBasis: '33.33%',
    flexShrink: 0,
  },
  secondaryHeading: {
    fontSize: theme.typography.pxToRem(15),
    color: theme.palette.text.secondary,
  },
  form: {
    width: '100%',
  },
  sectionTitle: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(2),
    fontWeight: 600,
    color: theme.palette.primary.main,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  sectionPaper: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    backgroundColor: theme.palette.grey[50],
    border: `1px solid ${theme.palette.grey[200]}`,
  },
  statusChip: {
    marginLeft: 'auto',
    fontWeight: 500,
  },
  successChip: {
    backgroundColor: theme.palette.success.main,
    color: theme.palette.success.contrastText,
    '& .MuiChip-icon': {
      color: theme.palette.success.contrastText,
    },
  },
  errorText: {
    fontSize: '0.75rem',
    marginTop: 3,
    marginLeft: 14,
    color: theme.palette.error.main,
  },
  accordionSummary: {
    '& .MuiAccordionSummary-content': {
      alignItems: 'center',
      gap: theme.spacing(2),
    },
  },
  stepper: {
    padding: theme.spacing(3, 0, 5),
  },
  stepContent: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  buttonGroup: {
    marginTop: theme.spacing(3),
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(1),
  },
  button: {
    marginLeft: theme.spacing(1),
  },
  dialogImage: {
    textAlign: 'center',
    padding: theme.spacing(2),
  },
  referenceImageDialog: {
    width: '100%',
    maxWidth: 500,
    height: 'auto',
    borderRadius: theme.spacing(1),
  },
}));

type Props = {
  code: string,
  process: SignProcess,
  expanded: boolean,
  disabled: boolean,
  tuFacturero: ?boolean,
  onExpanded: (string) => (event: any, isExpanded: boolean) => void,
  onSave: (SignProcess, boolean) => void,
};

const ProcessPersonalDataStepper = (props: Props) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const { code, process, expanded, disabled, onExpanded, onSave, tuFacturero = false } = props;
  const [activeStep, setActiveStep] = React.useState(0);
  const [confirmDialogOpen, setConfirmDialogOpen] = React.useState(false);
  const [confirmationData, setConfirmationData] = React.useState({
    legalID: '',
    idDocumentCode: '',
  });
  const [pendingValues, setPendingValues] = React.useState <? any > ();
  const [helpDialogOpen, setHelpDialogOpen] = React.useState(false);
  const [helpDialogImage, setHelpDialogImage] = React.useState <? string > ();
  const [helpDialogTitle, setHelpDialogTitle] = React.useState <? string > ();

  const getSteps = () => {
    const steps = tuFacturero ? [t('Identification'), t('Basic Information'), t('Address')] : [t('Basic Information'), t('Identification'), t('Address'), t('Personal Details')];
    return steps;
  };

  const handleStep = (step) => () => {
    setActiveStep(step);
  };

  const steps = getSteps();
  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };
  const validate = values => {
    const errors = {};
    if (activeStep >= (steps.length - 1)) {
      if (!values.name) {
        errors.name = t('Required');
      } else if (isNotValidName(values.name)) {
        errors.name = t('Invalid name');
      }
      if (!values.lastname) {
        errors.lastname = t('Required');
      } else if (isNotValidName(values.lastname)) {
        errors.lastname = t('Invalid lastname');
      }
      if (!values.email) {
        errors.email = t('Required');
      } else if (isNotValidEmail(values.email)) {
        errors.email = t('Invalid email address');
      }
      if (!values.phone) {
        errors.phone = t('Required');
      } else if (isNotValidPhone(values.phone)) {
        errors.phone = t('Invalid phone');
      }
    }
    return errors;
  };
  const onSubmit = async (values, { setSubmitting }) => {
    let data = process;
    const verify = data.idDocumentCode !== values.idDocumentCode
    if (tuFacturero && activeStep === 0 && verify) {
      // Preparar datos para confirmación
      setConfirmationData({
        legalID: values.legalID || '',
        idDocumentCode: values.idDocumentCode || '',
      });
      setPendingValues(values);
      setConfirmDialogOpen(true);
      setSubmitting(false);
      handleNext();
      return;
    }
    if (activeStep < (steps.length - 1)) {
      handleNext();
      setSubmitting(false);
      return;
    }
    data.legalIDType = values.legalIDType;
    data.legalIDExpiresAt = values.legalIDExpiresAt
      ? format(new Date(values.legalIDExpiresAt), 'dd/MM/yyyy') : null;
    data.legalID = values.legalID;
    if (data.legalIDType === 'PASSPORT') {
      data.legalIDEC = values.legalIDEC;
    }
    data.idDocumentCode = values.idDocumentCode;
    data.targets = Object.values(values.targets);
    data.name = values.name;
    data.lastname = values.lastname;
    data.email = values.email;
    data.phone = values.phone;
    data.countryOfResidence = values.countryOfResidence ? values.countryOfResidence.label : '';
    data.provinceOfResidence = typeof values.provinceOfResidence === 'string' ? values.provinceOfResidence : (values.provinceOfResidence?.label || values.provinceOfResidence || '');
    data.cityOfResidence = typeof values.cityOfResidence === 'string' ? values.cityOfResidence : (values.cityOfResidence?.label || values.cityOfResidence || '');
    data.addressOfResidence = values.addressOfResidence;
    data.zipCode = values.zipCode;
    data.forInvoicing = !!values.forInvoicing;
    data.tributaryName = values.tributaryName;
    data.tributaryLegalID = values.tributaryLegalID;
    data.gender = values.gender;
    data.birthday = values.birthday ? format(new Date(values.birthday), 'dd/MM/yyyy') : null;
    data.countryOfBirth = values.countryOfBirth ? values.countryOfBirth.label : '';
    onSave(data, false);
    setSubmitting(false);
  };

  const handleConfirmSave = () => {
    if (!!pendingValues) {
      setConfirmDialogOpen(false);
      let data = process;
      data.legalIDType = pendingValues.legalIDType;
      data.legalIDExpiresAt = pendingValues.legalIDExpiresAt ? format(new Date(pendingValues.legalIDExpiresAt), 'dd/MM/yyyy') : null;
      data.legalID = confirmationData.legalID;
      if (data.legalIDType === 'PASSPORT') {
        data.legalIDEC = pendingValues.legalIDEC;
      }
      data.idDocumentCode = confirmationData.idDocumentCode;
      onSave(data, true);
      setPendingValues(null);
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    }
  };

  const handleCancelSave = () => {
    handleBack();
    setConfirmDialogOpen(false);
    setPendingValues(null);
  };

  const handleOpenHelp = (imageUrl: string, title: string) => {
    setHelpDialogImage(imageUrl);
    setHelpDialogTitle(title);
    setHelpDialogOpen(true);
  };

  const handleCloseHelp = () => {
    setHelpDialogOpen(false);
    setHelpDialogImage(null);
    setHelpDialogTitle(null);
  };

  let processStatusText = process.hasDataLoaded() ? 'Completed' : 'Not completed';
  let processStatusIcon = process.hasDataLoaded() ? <DoneIcon /> : <EditIcon />;
  let statusColor = process.hasDataLoaded() ? 'primary' : 'default';

  if (process.hasDataValidated()) {
    processStatusText = 'Validated';
    processStatusIcon = <DoneAllIcon />;
    statusColor = 'success';
  }

  const handleIDChange = (e, name, values, handleChange, setFieldValue) => {
    handleChange(e);
    if (name.indexOf('signTarget') === 0) {
      if (!!values.targets[name]) {
        values.targets[name].value = e.currentTarget.checked;
      }
      setFieldValue('targets', values.targets);
    }
    const legalID = name === 'legalID'
      ? e.currentTarget.value
      : values.legalID;
    const tributaryLegalID = name === 'tributaryLegalID'
      ? e.currentTarget.dataset.value
      : values.tributaryLegalID;
    const legalIDType = name === 'legalIDType'
      ? e.currentTarget.dataset.value
      : values.legalIDType;
    const forInvoicing = name === 'forInvoicing'
      ? e.currentTarget.checked
      : values.forInvoicing;
    if ('EC_CEDULA' === legalIDType
      && forInvoicing
      && legalID.length === 10) {
      setFieldValue('tributaryLegalID', legalID + '001');
    } else if ('EC_RUC' === legalIDType
      && forInvoicing
      && legalID.length === 13
      && !tributaryLegalID) {
      setFieldValue('tributaryLegalID', legalID);
    }
  };

  const processTargets = {};
  if (process.targets) {
    process.targets.forEach(signTarget => processTargets[signTarget.code] = signTarget);
  }

  const getStepContent = (step, values, errors, touched, handleChange, handleBlur, setFieldValue) => {
    switch (step) {
      case (tuFacturero ? 1 : 0):
        return (
          <Grid container spacing={2}>
            {/* Targets Section */}
            {Object.values(values.targets).length > 0 && (
              <Grid item xs={12}>
                <Paper className={classes.sectionPaper} elevation={0}>
                  <Typography variant="subtitle2" gutterBottom color="textSecondary">
                    {t('Select the documents to sign')}
                  </Typography>
                  {tuFacturero ? (
                    <Typography variant="body1" className={classes.helperText}>
                      {t('Facturación electrónica gestionada por TuFacturero.')}
                    </Typography>
                  ) :
                    Object.values(values.targets).map(signTarget => (
                      <FormControlLabel
                        key={signTarget.code}
                        control={<Checkbox
                          disabled={disabled}
                          checked={signTarget.value}
                          onChange={(e) => handleIDChange(e, signTarget.code, values, handleChange, setFieldValue)}
                          onBlur={handleBlur}
                          name={signTarget.code}
                          color="primary" />}
                        label={<strong>{signTarget.text}</strong>}
                        style={{ display: 'block', marginBottom: 8 }}
                      />
                    ))}

                </Paper>
              </Grid>
            )}

            <Grid item xs={12} sm={6}>
              <TextField
                disabled={disabled}
                autoComplete="name"
                name="name"
                variant="outlined"
                required
                fullWidth
                id="name"
                label={t('Name')}
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.name}
                error={errors.name && touched.name}
                helperText={errors.name && touched.name && errors.name}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                disabled={disabled}
                autoComplete="lastname"
                name="lastname"
                variant="outlined"
                required
                fullWidth
                id="lastname"
                label={t('Lastname')}
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.lastname}
                error={errors.lastname && touched.lastname}
                helperText={errors.lastname && touched.lastname && errors.lastname}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                disabled={disabled}
                variant="outlined"
                required
                fullWidth
                id="email"
                label={t('Email')}
                name="email"
                autoComplete="email"
                type="email"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.email}
                error={errors.email && touched.email}
                helperText={errors.email && touched.email && errors.email}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                disabled={disabled}
                variant="outlined"
                required
                fullWidth
                id="phone"
                label={t('Phone')}
                name="phone"
                autoComplete="phone"
                type="tel"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.phone}
                error={errors.phone && touched.phone}
                helperText={errors.phone && touched.phone && errors.phone}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PhoneIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>
        );

      case (tuFacturero ? 0 : 1):
        return (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControl
                fullWidth
                variant="outlined"
                disabled={disabled}
                error={errors.legalIDType && touched.legalIDType}
              >
                <InputLabel id="legalIDTypeLabel">
                  {t('Legal ID type')}
                </InputLabel>
                <Select
                  labelId="legalIDTypeLabel"
                  label={t('Legal ID type')}
                  id="legalIDType"
                  name="legalIDType"
                  value={values.legalIDType}
                  onChange={(e) => handleIDChange(e, 'legalIDType', values, handleChange, setFieldValue)}
                  startAdornment={
                    <InputAdornment position="start">
                      <BadgeIcon color="action" />
                    </InputAdornment>
                  }
                >
                  {process.validLegalIDTypes.map((item) => (
                    <MenuItem key={item} value={item}>
                      {t(toSentence(item))}
                    </MenuItem>
                  ))}
                </Select>
                {errors.legalIDType && touched.legalIDType && (
                  <Typography className={classes.errorText}>
                    {errors.legalIDType}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                disabled={disabled}
                variant="outlined"
                fullWidth
                id="legalID"
                label={t('Legal ID')}
                name="legalID"
                autoComplete="legalID"
                onChange={(e) => handleIDChange(e, 'legalID', values, handleChange, setFieldValue)}
                onBlur={handleBlur}
                value={values.legalID}
                error={errors.legalID && touched.legalID}
                helperText={errors.legalID && touched.legalID && errors.legalID}
              />
            </Grid>

            {
              values.legalIDType === 'PASSPORT' && (
                <Grid item xs={12} sm={6}>
                  <TextField
                    disabled={disabled}
                    variant="outlined"
                    fullWidth
                    id="legalIDEC"
                    label={t('Legal EC')}
                    name="legalIDEC"
                    autoComplete="legalIDEC"
                    onChange={(e) => handleIDChange(e, 'legalIDEC', values, handleChange, setFieldValue)}
                    onBlur={handleBlur}
                    value={values.legalIDEC}
                    error={errors.legalIDEC && touched.legalIDEC}
                    helperText={errors.legalIDEC && touched.legalIDEC && errors.legalIDEC}
                  />
                </Grid>
              )
            }


            <Grid item xs={12} sm={6}>
              <MuiPickersUtilsProvider utils={DateFnsUtils}>
                <DatePicker
                  disabled={disabled}
                  inputVariant="outlined"
                  fullWidth
                  id="legalIDExpiresAt"
                  label={t('Legal ID expires at')}
                  name="legalIDExpiresAt"
                  onChange={(value) => {
                    setFieldValue('legalIDExpiresAt', value);
                  }}
                  onBlur={handleBlur}
                  value={values.legalIDExpiresAt}
                  format="dd/MM/yyyy"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <CalendarIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                  KeyboardButtonProps={{
                    'aria-label': t('Change legal ID expiration'),
                  }} />
              </MuiPickersUtilsProvider>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                disabled={disabled}
                variant="outlined"
                fullWidth
                id="idDocumentCode"
                label={t('ID Document Code')}
                name="idDocumentCode"
                autoComplete="idDocumentCode"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.idDocumentCode}
                error={errors.idDocumentCode && touched.idDocumentCode}
                helperText={errors.idDocumentCode && touched.idDocumentCode && errors.idDocumentCode}
              />
            </Grid>
          </Grid>
        );

      case 2:
        return (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                disabled={disabled}
                fullWidth
                id="countryOfResidence"
                name="countryOfResidence"
                options={countries}
                autoHighlight
                getOptionLabel={(option) => option.label}
                getOptionSelected={(option, value) => option && value && option.code === value.code}
                value={values.countryOfResidence}
                renderOption={(option) => (
                  <><span>{option.flag()}</span>&nbsp;{option.label}</>
                )}
                onChange={(e, value) => {
                  setFieldValue('countryOfResidence', value);
                  // Clear province and city when country changes
                  setFieldValue('provinceOfResidence', '');
                  setFieldValue('cityOfResidence', '');
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('Country of residence')}
                    variant="outlined"
                    onBlur={handleBlur}
                    error={errors.countryOfResidence && touched.countryOfResidence}
                    helperText={errors.countryOfResidence && touched.countryOfResidence && errors.countryOfResidence}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <InputAdornment position="start">
                            <FlagIcon color="action" />
                          </InputAdornment>
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                    inputProps={{
                      ...params.inputProps,
                      autoComplete: 'nothing-at-all',
                    }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              {(() => {
                const provincesObj = getProvincesOfCountry(values.countryOfResidence?.code) || {};
                const provinces = Object.values(provincesObj);
                const hasProvinces = provinces.length > 0;

                if (hasProvinces) {
                  const provinceValue = typeof values.provinceOfResidence === 'string'
                    ? values.provinceOfResidence
                    : (values.provinceOfResidence?.label || '');

                  return (
                    <Autocomplete
                      disabled={disabled}
                      fullWidth
                      freeSolo
                      id="provinceOfResidence"
                      name="provinceOfResidence"
                      options={provinces}
                      autoHighlight
                      getOptionLabel={(option) => option}
                      value={provinceValue}
                      onChange={(e, value) => {
                        setFieldValue('provinceOfResidence', value);
                        setFieldValue('cityOfResidence', '');
                      }}
                      onInputChange={(e, value) => {
                        if (e && e.type === 'change') {
                          setFieldValue('provinceOfResidence', value);
                        }
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={t('Province of residence')}
                          variant="outlined"
                          onBlur={handleBlur}
                          error={errors.provinceOfResidence && touched.provinceOfResidence}
                          helperText={errors.provinceOfResidence && touched.provinceOfResidence && errors.provinceOfResidence}
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                              <>
                                <InputAdornment position="start">
                                  <LocationCityIcon color="action" />
                                </InputAdornment>
                                {params.InputProps.startAdornment}
                              </>
                            ),
                          }}
                          inputProps={{
                            ...params.inputProps,
                            autoComplete: 'nothing-at-all',
                          }}
                        />
                      )}
                    />
                  );
                }

                const textValue = typeof values.provinceOfResidence === 'string'
                  ? values.provinceOfResidence
                  : (values.provinceOfResidence?.label || '');

                return (
                  <TextField
                    disabled={disabled}
                    variant="outlined"
                    fullWidth
                    id="provinceOfResidence"
                    label={t('Province of residence')}
                    name="provinceOfResidence"
                    autoComplete="provinceOfResidence"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={textValue}
                    error={errors.provinceOfResidence && touched.provinceOfResidence}
                    helperText={errors.provinceOfResidence && touched.provinceOfResidence && errors.provinceOfResidence}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LocationCityIcon color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />
                );
              })()}
            </Grid>

            <Grid item xs={12} sm={6}>
              {(() => {
                const provinceName = typeof values.provinceOfResidence === 'string'
                  ? values.provinceOfResidence
                  : (values.provinceOfResidence?.label || '');

                const cities = getCitiesOfProvince(provinceName) || [];
                const hasCities = cities.length > 0;

                if (hasCities) {
                  const cityValue = values.cityOfResidence || '';

                  return (
                    <Autocomplete
                      disabled={disabled}
                      fullWidth
                      freeSolo
                      id="cityOfResidence"
                      name="cityOfResidence"
                      options={cities}
                      autoHighlight
                      getOptionLabel={(option) => option}
                      value={cityValue}
                      onChange={(e, value) => {
                        setFieldValue('cityOfResidence', value);
                      }}
                      onInputChange={(e, value) => {
                        if (e && e.type === 'change') {
                          setFieldValue('cityOfResidence', value);
                        }
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={t('City of residence')}
                          variant="outlined"
                          onBlur={handleBlur}
                          error={errors.cityOfResidence && touched.cityOfResidence}
                          helperText={errors.cityOfResidence && touched.cityOfResidence && errors.cityOfResidence}
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                              <>
                                <InputAdornment position="start">
                                  <LocationCityIcon color="action" />
                                </InputAdornment>
                                {params.InputProps.startAdornment}
                              </>
                            ),
                          }}
                          inputProps={{
                            ...params.inputProps,
                            autoComplete: 'nothing-at-all',
                          }}
                        />
                      )}
                    />
                  );
                }

                return (
                  <TextField
                    disabled={disabled}
                    variant="outlined"
                    fullWidth
                    id="cityOfResidence"
                    label={t('City of residence')}
                    name="cityOfResidence"
                    autoComplete="cityOfResidence"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.cityOfResidence}
                    error={errors.cityOfResidence && touched.cityOfResidence}
                    helperText={errors.cityOfResidence && touched.cityOfResidence && errors.cityOfResidence}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LocationCityIcon color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />
                );
              })()}
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                disabled={disabled}
                variant="outlined"
                fullWidth
                id="zipCode"
                label={t('Zip code')}
                name="zipCode"
                autoComplete="zipCode"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.zipCode}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                disabled={disabled}
                variant="outlined"
                fullWidth
                id="addressOfResidence"
                label={t('Address of residence')}
                name="addressOfResidence"
                autoComplete="addressOfResidence"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.addressOfResidence}
                error={errors.addressOfResidence && touched.addressOfResidence}
                helperText={errors.addressOfResidence && touched.addressOfResidence && errors.addressOfResidence}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <HomeIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>
        );

      case 3:
        return (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControl
                fullWidth
                variant="outlined"
                disabled={disabled}
                error={errors.gender && touched.gender}
              >
                <InputLabel id="genderLabel">
                  {t('Gender')}
                </InputLabel>
                <Select
                  labelId="genderLabel"
                  label={t('Gender')}
                  id="gender"
                  name="gender"
                  value={values.gender}
                  onChange={handleChange}
                  startAdornment={
                    <InputAdornment position="start">
                      <GenderIcon color="action" />
                    </InputAdornment>
                  }
                >
                  {process.genders.map((item) => (
                    <MenuItem key={item} value={item}>
                      {t(item)}
                    </MenuItem>
                  ))}
                </Select>
                {errors.gender && touched.gender && (
                  <Typography className={classes.errorText}>
                    {errors.gender}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <MuiPickersUtilsProvider utils={DateFnsUtils}>
                <DatePicker
                  disabled={disabled}
                  inputVariant="outlined"
                  fullWidth
                  id="birthday"
                  label={t('Birthday')}
                  name="birthday"
                  onChange={(value) => {
                    setFieldValue('birthday', value);
                  }}
                  onBlur={handleBlur}
                  value={values.birthday}
                  format="dd/MM/yyyy"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <CalendarIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                  KeyboardButtonProps={{
                    'aria-label': t('Change birthday'),
                  }} />
              </MuiPickersUtilsProvider>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Autocomplete
                disabled={disabled}
                fullWidth
                id="countryOfBirth"
                name="countryOfBirth"
                options={countries}
                autoHighlight
                getOptionLabel={(option) => option.label}
                value={values.countryOfBirth}
                renderOption={(option) => (
                  <><span>{option.flag()}</span>&nbsp;{option.label}</>
                )}
                onChange={(e, value) => {
                  setFieldValue('countryOfBirth', value);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('Country of birth')}
                    variant="outlined"
                    onBlur={handleBlur}
                    error={errors.countryOfBirth && touched.countryOfBirth}
                    helperText={errors.countryOfBirth && touched.countryOfBirth && errors.countryOfBirth}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <InputAdornment position="start">
                            <FlagIcon color="action" />
                          </InputAdornment>
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                    inputProps={{
                      ...params.inputProps,
                      autoComplete: 'nothing-at-all',
                    }}
                  />
                )}
              />
            </Grid>
          </Grid>
        );

      default:
        return t('Unknown step');
    }
  };

  return (
    <Formik
      initialValues={{
        targets: processTargets,
        name: process.name,
        lastname: process.lastname,
        email: process.email,
        phone: process.phone,
        legalIDType: process.legalIDType || '',
        legalID: process.legalID || '',
        legalIDEC: process.legalIDEC || '',
        legalIDExpiresAt: process.legalIDExpiresAt,
        idDocumentCode: process.idDocumentCode || '',
        countryOfResidence: Country.find(countries, process.countryOfResidence),
        provinceOfResidence: process.provinceOfResidence || '',
        cityOfResidence: process.cityOfResidence || '',
        addressOfResidence: process.addressOfResidence || '',
        zipCode: process.zipCode || '',
        forInvoicing: !!process.forInvoicing,
        tributaryName: process.tributaryName || '',
        tributaryLegalID: process.tributaryLegalID || '',
        gender: process.gender,
        birthday: process.birthday,
        countryOfBirth: Country.find(countries, process.countryOfBirth),
      }}
      validate={validate}
      onSubmit={onSubmit}
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        handleSubmit,
        setFieldValue,
        isSubmitting,
      }) => (
        <form className={classes.form} onSubmit={handleSubmit}>
          <Accordion
            key={code}
            expanded={expanded}
            onChange={onExpanded(code)}
            TransitionProps={{ unmountOnExit: true }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls={`${code}-panel`}
              id={`${code}-panel`}
              className={classes.accordionSummary}
            >
              <Typography className={classes.heading} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <PersonIcon /> {t('Personal data')}
              </Typography>
              <Chip
                icon={processStatusIcon}
                label={t(processStatusText)}
                color={statusColor === 'success' ? 'default' : statusColor}
                size="small"
                className={`${classes.statusChip} ${statusColor === 'success' ? classes.successChip : ''}`}
              />
            </AccordionSummary>
            <AccordionDetails>
              <div className={classes.form}>
                <Stepper activeStep={activeStep} className={classes.stepper} nonLinear={!tuFacturero} alternativeLabel>
                  {steps.map((label, index) => (
                    <Step key={label}>
                      <StepButton color="inherit" onClick={handleStep(index)} > {label} </StepButton>
                    </Step>
                  ))}
                </Stepper>
                <div>
                  <div className={classes.stepContent}>
                    {getStepContent(activeStep, values, errors, touched, handleChange, handleBlur, setFieldValue)}
                  </div>
                  <div className={classes.buttonGroup}>
                    <Button
                      disabled={activeStep === 0}
                      onClick={handleBack}
                      className={classes.button}
                    >
                      {t('Back')}
                    </Button>
                    <Button
                      type="submit"
                      disabled={(disabled || isSubmitting) && (activeStep === (steps.length - 1))}
                      variant="contained"
                      color="primary"
                      startIcon={activeStep === (steps.length - 1) ? <DoneIcon /> : <NextIcon />}
                      className={classes.button}
                    >
                      {activeStep === (steps.length - 1) ? t('Save') : t('Next')}
                    </Button>
                  </div>
                </div>
              </div>
            </AccordionDetails>
          </Accordion>

          {/* Dialog de confirmación */}
          <DataValidationConfirmDialog
            open={confirmDialogOpen}
            legalID={confirmationData.legalID}
            idDocumentCode={confirmationData.idDocumentCode}
            onLegalIDChange={(value) => setConfirmationData({ ...confirmationData, legalID: value })}
            onIdDocumentCodeChange={(value) => setConfirmationData({ ...confirmationData, idDocumentCode: value })}
            onConfirm={handleConfirmSave}
            onCancel={handleCancelSave}
            onOpenHelp={handleOpenHelp}
          />

          {/* Dialog de ayuda con imagen */}
          <Dialog
            open={helpDialogOpen}
            onClose={handleCloseHelp}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>{helpDialogTitle}</DialogTitle>
            <DialogContent className={classes.dialogImage}>
              {!!helpDialogImage && (
                <img
                  src={helpDialogImage}
                  alt={helpDialogTitle}
                  className={classes.referenceImageDialog}
                  onError={(e) => { e.currentTarget.src = `${process.env.PUBLIC_URL || ''}/images/placeholder.png`; }}
                />
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseHelp} color="primary">
                {t('Cerrar')}
              </Button>
            </DialogActions>
          </Dialog>
        </form>
      )}
    </Formik>
  );
};

export default ProcessPersonalDataStepper;
